import { memo, SyntheticEvent, useCallback, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import type { Slug } from '@bridebook/toolbox/src/types';
import { IStylingProps } from '../../../themes/types';
import { getProportionedImagePadding } from '../../../utils/get-propotioned-image-padding';
import useNativeLazyLoading from '../../../utils/hooks/use-native-lazy-loading';
import { FelaCSS } from '../../fela/flowtypes';
import componentStyles from './imagex.style';
import { handleImgLoading, type ILoadingType } from './handle-img-loading';
import { useImageErrorHandling } from './use-image-error-handling';
import { cn } from '@bridebook/aura';
import { translateImgixToCloudflare } from '@bridebook/toolbox/src/buildImageUrl/translate-imgix-to-cloudflare';

export interface SharedTypes {
  style?: FelaCSS;
}

interface PropTypes extends SharedTypes, IStylingProps {
  allowPin?: boolean;
  w: number;
  h: number;
  altText?: string;
  imgTitle?: string;
  customParams?: object;
  fit?: string;
  crop?: string;
  quality?: number;
  slug?: Slug;
  src: string;
  onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onError?: (event: SyntheticEvent<HTMLImageElement>) => void;
  loading?: ILoadingType;
  imageStyle?: FelaCSS;
  forceCleanSrc?: boolean; // Do not add any additional params to the original src
  classNames?: {
    wrapper?: string;
    img?: string;
  };
  isSSR?: boolean;
  shimmer?: boolean;
}

const ImageX = ({
  allowPin,
  w: imgW,
  h: imgH,
  altText,
  imgTitle,
  customParams = {},
  fit = 'crop',
  crop,
  quality,
  slug = 'photo',
  src: initSrc,
  onLoad,
  style,
  onError,
  loading = 'eager',
  imageStyle,
  forceCleanSrc = false,
  classNames,
  isSSR,
  shimmer,
  ...restProps
}: PropTypes) => {
  const supportsNativeLazyLoading = useNativeLazyLoading();

  const [ref, inView] = useInView({
    triggerOnce: true,
    rootMargin: '200px',
    initialInView: loading == 'eager',
    skip: loading == 'eager',
  });

  const imgSrc = useMemo(() => {
    if (forceCleanSrc) {
      return initSrc;
    }

    return translateImgixToCloudflare({
      src: initSrc,
      crop,
      fit,
      slug,
      w: imgW,
      h: imgH,
      customParams,
      forceCleanSrc,
      quality,
    });
  }, [initSrc, crop, fit, slug, imgW, imgH, customParams, forceCleanSrc, quality]);

  const { imageSrc, isLoaded, handleError, handleLoad } = useImageErrorHandling(imgSrc, onError);

  const paddingBottom = useMemo(
    () => getProportionedImagePadding({ width: imgW, height: imgH }),
    [imgW, imgH],
  );

  const imgLoadProps = useMemo(
    () =>
      handleImgLoading(loading, {
        src: imageSrc,
        inView,
        supportsNativeLazyLoading,
      }),
    [loading, imageSrc, inView, supportsNativeLazyLoading],
  );

  const styles = useMemo(
    () => componentStyles({ paddingBottom, style, imageStyle, isLoaded: isLoaded || isSSR }),
    [paddingBottom, style, imageStyle, isLoaded, isSSR],
  );

  const handleImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      handleLoad();
      onLoad?.(event);
    },
    [handleLoad, onLoad],
  );

  return (
    <div
      style={styles.wrapper}
      ref={ref}
      {...restProps}
      className={cn(restProps.className, classNames?.wrapper)}>
      {shimmer && !isLoaded && !isSSR && <div className="shimmer absolute inset-0" />}
      <img
        style={styles.image}
        className={cn('img-el', classNames?.img)}
        data-pin-nopin={!allowPin}
        alt={altText}
        title={imgTitle}
        onLoad={handleImageLoad}
        draggable={false}
        onError={handleError}
        {...imgLoadProps}
        {...(isSSR && { src: imgSrc, loading: 'lazy' })}
      />
    </div>
  );
};

export default memo(ImageX);
